import { useCallback, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import { AdminHeader } from '@components/layout/admin-header'
import { useCreateTaxonomyTranslation } from '@global/hooks/mutations/taxonomy-translations'
import { Button } from '@taxonomy-translation/components'
import { ROUTES } from 'routes'
import { useTranslationUploadStore } from '../hooks'
import { FileObjective, TranslationUploadForm } from '../types'
import { CancelUploadConfirmationModal } from './cancel-upload-confirmation-modal'
import { Stepper, useStepperStore } from './stepper'
import { CandidateInput, ReferenceInput } from '@taxonomy-translation/types'
import { t } from '@lingui/core/macro'

const getSteps = () => [t`Data to be translated`, t`Baseline data`, t`Review`]

export const PageHeader = () => {
  const steps = getSteps()
  const history = useHistory()
  const {
    activeStep,
    completed,
    isCompleted,
    setActiveStep,
    setCompleted,
    setIsCompleted,
    resetStepper,
  } = useStepperStore()
  const {
    formState: { errors, dirtyFields },
    handleSubmit,
  } = useFormContext<TranslationUploadForm>()
  const { isLoading, setIsLoading, setId, setError, resetTranslationUpload } =
    useTranslationUploadStore()

  const { mutate: createTaxonomyTranslation } = useCreateTaxonomyTranslation()

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false)

  const stepTwoDisabled =
    !!errors.stepOne || !(dirtyFields.stepOne?.templates ?? dirtyFields.stepOne?.files)
  const stepThreeDisabled =
    stepTwoDisabled ||
    !!errors.stepTwo ||
    !(dirtyFields.stepTwo?.templates ?? dirtyFields.stepTwo?.files)

  const disabledSteps = [
    false, // first step always available
    stepTwoDisabled, // go to second
    stepThreeDisabled, // go to third
    stepThreeDisabled, // send data (no additional checks on third step, for now)
  ]

  const onNextClick = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1)

      const newCompleted = [...completed]
      newCompleted[activeStep] = true
      setCompleted(newCompleted)
    }

    // last step
    if (activeStep === steps.length - 1) {
      void handleSubmit(data => {
        setIsCompleted(true) // make all steps checked
        setIsLoading(true) // set loading state for third step

        let candidateInput: CandidateInput
        let referenceInput: ReferenceInput

        try {
          if (data.stepOne.dataType === 'templates') {
            if (data.stepOne.templates.length === 0) {
              throw new Error(t`No templates selected for candidate data.`)
            }

            candidateInput = {
              candidate_templates: data.stepOne.templates.map(({ id }) => `${id}`),
            }
          } else {
            const candidateTrafficking = data.stepOne.files.find(
              file => file.objective === FileObjective.DATA_DICTIONARY,
            )
            const candidateListValues = data.stepOne.files.find(
              file => file.objective === FileObjective.LIST_VALUES,
            )

            if (!candidateTrafficking) {
              throw new Error(t`Candidate trafficking file is required.`)
            }

            candidateInput = {
              candidate_trafficking: candidateTrafficking,
              ...(candidateListValues && { candidate_list_values: candidateListValues }),
            }
          }

          if (data.stepTwo.dataType === 'templates') {
            if (data.stepTwo.templates.length === 0) {
              throw new Error(t`No templates selected for reference data.`)
            }

            referenceInput = {
              reference_templates: data.stepTwo.templates.map(({ id }) => `${id}`),
            }
          } else {
            const referenceTrafficking = data.stepTwo.files.find(
              file => file.objective === FileObjective.DATA_DICTIONARY,
            )
            const referenceListValues = data.stepTwo.files.find(
              file => file.objective === FileObjective.LIST_VALUES,
            )

            if (!referenceTrafficking) {
              throw new Error(t`Reference trafficking file is required.`)
            }

            referenceInput = {
              reference_trafficking: referenceTrafficking,
              ...(referenceListValues && { reference_list_values: referenceListValues }),
            }
          }
        } catch (error) {
          setIsLoading(false)
          setIsCompleted(false)
          setError(error)

          return
        }

        const payload = {
          ...candidateInput,
          ...referenceInput,
        }

        // submit data
        createTaxonomyTranslation(payload, {
          onSuccess: response => {
            setId(response.uuid) // set id to be used in third step
            // NOTE: loading is not stoped here as translation in proggress and handled on third step
          },
          onError: error => {
            setIsLoading(false)
            setIsCompleted(false)
            setError(error)
          },
        })
      })()
    }
  }, [activeStep, completed, handleSubmit, createTaxonomyTranslation])

  const onPrevClick = () => {
    if (activeStep > 0) {
      // mark current step as not completed when we go to previous one
      const newCompleted = [...completed]
      newCompleted[activeStep] = false
      setCompleted(newCompleted)

      setActiveStep(activeStep - 1)
    }
  }

  const onStepClick = (nextActiveStep: number) => {
    // mark only next step as completed

    if (nextActiveStep > activeStep) {
      const newCompleted = [...completed]
      newCompleted[activeStep] = true
      setCompleted(newCompleted)
    }

    setActiveStep(nextActiveStep)
  }

  const onCancelClick = () => {
    setIsCancelModalOpen(false)
    onCloseClick()
  }

  const onCloseClick = () => {
    history.push(ROUTES.TAXONOMY_TRANSLATION)
    resetStepper()
    resetTranslationUpload()
  }

  // initialize completed state
  useEffect(() => {
    setCompleted([false, false, false])
  }, [])

  return (
    <AdminHeader>
      <div className="flex justify-between items-center gap-2 w-full h-[70px] px-8 pt-[10px]">
        <h2 className="font-bold text-slate-800 basis-1/4">{t`New taxonomy translation`}</h2>

        <Stepper
          activeStep={activeStep}
          steps={steps}
          completed={completed}
          disabled={disabledSteps}
          isCompleted={isCompleted}
          onStepClick={onStepClick}
          className="basis-1/2 justify-center"
        />

        <div className="flex justify-end items-center gap-2 basis-1/4">
          {isLoading ? (
            <Button variant="text" onClick={onCloseClick}>
              {t`Close`}
            </Button>
          ) : (
            <>
              <Button
                variant="text"
                onClick={() => {
                  setIsCancelModalOpen(true)
                }}
              >
                {t`Cancel`}
              </Button>
              <Button variant="outline" onClick={onPrevClick}>
                {t`Back`}
              </Button>
              <Button onClick={onNextClick} disabled={disabledSteps[activeStep + 1]}>
                {activeStep === steps.length - 1 ? t`Begin translation` : t`Next`}
              </Button>
            </>
          )}
          {isCancelModalOpen && (
            <CancelUploadConfirmationModal
              onClose={() => {
                setIsCancelModalOpen(false)
              }}
              onSubmit={onCancelClick}
            />
          )}
        </div>
      </div>
    </AdminHeader>
  )
}
